// Defensive date parsing for API timestamps that can arrive as ISO strings,
// epoch seconds, epoch milliseconds, null, or malformed values. NEVER yields an
// Invalid Date — callers render a truthful fallback ("Time unavailable").

export const TIME_UNAVAILABLE = "Time unavailable";

function fromEpoch(n) {
  if (!Number.isFinite(n) || n <= 0) return null;
  // Heuristic: >= 1e12 is milliseconds; smaller positive numbers are seconds.
  const ms = n >= 1e12 ? n : n * 1000;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Returns a valid Date or null (never an Invalid Date).
export function parseFlexibleDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") return fromEpoch(value);
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return null;
    // A purely numeric string is an epoch (seconds or milliseconds).
    if (/^\d{9,}$/.test(s)) return fromEpoch(Number(s));
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const DEFAULT_OPTS = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };

// Format an odds/snapshot timestamp truthfully. Returns TIME_UNAVAILABLE for any
// missing/malformed value instead of "Invalid Date".
export function formatSnapshotTime(value, opts = DEFAULT_OPTS) {
  const d = parseFlexibleDate(value);
  if (!d) return TIME_UNAVAILABLE;
  try { return d.toLocaleString(undefined, opts); } catch { return TIME_UNAVAILABLE; }
}

// Defensive PRESENTATION only: if backend agent copy leaks a raw ISO timestamp,
// reformat that substring into a friendly local time WITHOUT altering the
// factual instant or any other text. Unparseable matches are left untouched.
const ISO_IN_TEXT = /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;
export function humanizeTimestamps(text) {
  if (typeof text !== "string" || !text) return text;
  return text.replace(ISO_IN_TEXT, (m) => {
    const friendly = formatSnapshotTime(m);
    return friendly === TIME_UNAVAILABLE ? m : friendly;
  });
}
